// Google Maps JS API loader + the LabelOverlay subclass factory.
//
// The browser key comes from /api/maps-key at runtime, and LabelOverlay can
// only `extends google.maps.OverlayView` AFTER the script loads — so both are
// produced lazily and cached. The loader promise is memoized so repeated calls
// (HMR, multiple components) never inject the script twice.

import { fetchMapsKey } from '../api';
import type { LatLng, MapViewState } from '../types';

let loadPromise: Promise<typeof google> | null = null;

// Bootstrap callback name. The Maps script invokes this once the API — Map,
// Marker, OverlayView, etc. — is fully ready, which is the only reliable
// "everything is constructable" signal. (This is the proven legacy pattern;
// importLibrary added prod failure surface for no benefit here.)
const CALLBACK = '__gmapsReady__';

/** Idempotently loads the Maps JS API. Rejects if the key/script fails. */
export function loadGoogleMaps(): Promise<typeof google> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    if (typeof google !== 'undefined' && google.maps?.Map) return google;
    const key = await fetchMapsKey();
    if (!key) throw new Error('NO_KEY');
    await new Promise<void>((resolve, reject) => {
      (window as unknown as Record<string, () => void>)[CALLBACK] = () => resolve();
      const script = document.createElement('script');
      script.id = 'gmaps-sdk';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&callback=${CALLBACK}`;
      script.async = true;
      script.onerror = () => reject(new Error('SCRIPT_LOAD_FAILED'));
      document.head.appendChild(script);
    });
    return google;
  })();
  return loadPromise;
}

export interface LabelInstance extends google.maps.OverlayView {
  setText(text: string): void;
  setHouseholdSize(count: number): void;
  setPosition(pos: LatLng): void;
}

export type LabelKind = 'friend' | 'trip' | 'candidate' | 'suggestion';

export interface LabelCtor {
  new (position: LatLng, text: string, kind?: LabelKind, color?: string | null): LabelInstance;
}

let LabelOverlayClass: LabelCtor | null = null;

/** Returns the LabelOverlay class, defining it once the Maps API is present. */
export function getLabelOverlayClass(): LabelCtor {
  if (LabelOverlayClass) return LabelOverlayClass;

  class LabelOverlay extends google.maps.OverlayView {
    private _pos: LatLng;
    private _text: string;
    private _kind: LabelKind | '';
    private _color: string | null;
    private _householdSize = 1;
    private _div: HTMLDivElement | null = null;

    constructor(position: LatLng, text: string, kind?: LabelKind, color?: string | null) {
      super();
      this._pos = position;
      this._text = text;
      this._kind = kind || '';
      this._color = color || null;
    }

    onAdd() {
      const div = document.createElement('div');
      div.className = 'map-label ' + this._kind;
      div.textContent = this._text;
      // Candidate labels tint via a CSS custom prop (cream pill, dark text);
      // other kinds get a solid background.
      if (this._color) {
        if (this._kind === 'candidate') div.style.setProperty('--label-accent', this._color);
        else div.style.background = this._color;
      }
      this._div = div;
      this.setHouseholdSize(this._householdSize);
      this.getPanes()!.floatPane.appendChild(div);
    }

    draw() {
      if (!this._div) return;
      const proj = this.getProjection();
      if (!proj) return;
      const px = proj.fromLatLngToDivPixel(new google.maps.LatLng(this._pos.lat, this._pos.lng));
      if (px) {
        this._div.style.left = px.x + 'px';
        this._div.style.top = px.y + 'px';
      }
    }

    onRemove() {
      if (this._div && this._div.parentNode) this._div.parentNode.removeChild(this._div);
      this._div = null;
    }

    setHouseholdSize(count: number) {
      this._householdSize = count;
      this._div?.style.setProperty('--friend-label-offset', `${Math.min(3, count) * 11 + 6}px`);
    }

    setText(text: string) {
      this._text = text;
      if (this._div) this._div.textContent = text;
    }

    setPosition(pos: LatLng) {
      this._pos = pos;
      this.draw();
    }
  }

  LabelOverlayClass = LabelOverlay as unknown as LabelCtor;
  return LabelOverlayClass;
}

// A single shared reference to the live map (not reconciled, so not a store).
let currentMap: google.maps.Map | null = null;
export function setCurrentMap(m: google.maps.Map): void {
  currentMap = m;
}
export function getCurrentMap(): google.maps.Map | null {
  return currentMap;
}

/** Creates the map instance with our chrome-free styling. */
export function createMap(el: HTMLElement, view: MapViewState): google.maps.Map {
  return new google.maps.Map(el, {
    center: { lat: view.lat, lng: view.lng },
    zoom: view.zoom,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: false, // we render our own
    styles: [{ featureType: 'poi', stylers: [{ visibility: 'simplified' }] }],
  });
}
